import { Link } from 'react-router-dom'
import { BYO_KEY_INVALID_CTAS, QUOTA_CTAS, type QuotaCta } from '../lib/config'
import type { QuotaGateState } from '../lib/quota'
import ApiKeyFixInline from './ApiKeyFixInline'

interface QuotaGateProps {
  gate: QuotaGateState
  /** 'banner' = compact inline strip; 'card' = centered block for a flow's failure surface. */
  variant?: 'banner' | 'card'
  /**
   * When provided on an invalid-key gate, the user fixes the key IN PLACE (an
   * inline verify + save form) and this callback resumes the stalled action so
   * they land back where they were. When omitted, the gate falls back to a
   * "Update your API key" link to Settings (e.g. banner contexts).
   */
  onKeyResolved?: () => void | Promise<void>
}

function ctaClass(emphasis: QuotaCta['emphasis']): string {
  return emphasis === 'primary'
    ? 'inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-sm font-medium text-on-primary hover:opacity-90 transition'
    : 'inline-flex items-center justify-center rounded-lg border border-outline-variant px-4 py-2 text-sm font-medium text-on-surface hover:bg-surface-container-high transition'
}

function CtaButton({ cta }: { cta: QuotaCta }) {
  if (cta.to) {
    return (
      <Link to={cta.to} className={ctaClass(cta.emphasis)}>
        {cta.label}
      </Link>
    )
  }
  return (
    <a href={cta.href} className={ctaClass(cta.emphasis)}>
      {cta.label}
    </a>
  )
}

interface GateCopy {
  heading: string
  body: string
  ctas: QuotaCta[]
}

function gateCopy(gate: QuotaGateState): GateCopy {
  if (gate.kind === 'byo_key_invalid') {
    return {
      heading: 'Your API key needs attention',
      body: 'Your API key is invalid or has been revoked. Enter a new key below to pick up where you left off.',
      ctas: BYO_KEY_INVALID_CTAS,
    }
  }
  const isGlobal = gate.source === 'global_budget'
  return {
    heading: isGlobal ? 'AI features are paused' : "You've reached your usage limit",
    body: isGlobal
      ? 'AI-powered features are temporarily paused while we top up capacity. Your Library and existing CVs are unaffected.'
      : 'AI-powered features are paused for now. You can still browse your Library and download existing CVs.',
    ctas: QUOTA_CTAS,
  }
}

/**
 * The single quota / invalid-key wall UI. Render it from a QuotaGateState
 * produced by the lib/quota mapper — never construct it from a raw error code in
 * a component.
 */
export default function QuotaGate({ gate, variant = 'card', onKeyResolved }: QuotaGateProps) {
  // In-place recovery: on an invalid-key gate with a resume callback, show the
  // key form + "Contact admin" instead of a link that navigates away.
  const inlineFix = gate.kind === 'byo_key_invalid' && onKeyResolved !== undefined
  const { heading, body, ctas } = gateCopy(gate)
  const shownCtas = inlineFix ? ctas.filter((c) => c.kind !== 'use_byo_key') : ctas

  const ctaRow = (
    <div className={variant === 'banner' ? 'mt-2 flex flex-wrap gap-2' : 'mt-5 flex flex-wrap justify-center gap-3'}>
      {shownCtas.map((cta) => (
        <CtaButton key={cta.kind} cta={cta} />
      ))}
    </div>
  )

  if (variant === 'banner') {
    return (
      <div
        role="alert"
        className="mx-4 mt-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <span className="font-medium">{heading}.</span> {body}
        {ctaRow}
      </div>
    )
  }

  return (
    <div role="alert" className="mx-auto flex max-w-md flex-col items-center text-center">
      <h2 className="font-headline text-xl font-semibold text-on-surface">{heading}</h2>
      <p className="mt-2 text-sm text-secondary">{body}</p>
      {inlineFix && onKeyResolved && <ApiKeyFixInline onResolved={onKeyResolved} />}
      {ctaRow}
    </div>
  )
}
